import { toast } from "@/hooks/use-toast.ts";
import { constructLocaleDict, fetchMainLanguage, LocaleDict, SupportedLocale } from "@/i18n";
import api from "@/lib/axiosConfig.ts";
import type { ArtworkAuthenticationCmsDto } from "@/lib/cms/artwork-authentication-cms";
import { cmsMediaTranslationKey } from "@/lib/cms/cms-media-labels";
import { consumeCmsMediaUploaded } from "@/lib/cms/cms-media-upload";
import {
  isDrawerAppearanceSavePending,
  notifyDrawerAppearanceMutationResult,
  notifyDrawerAppearanceValidationFailed,
} from "@/lib/cms/cms-page-appearance-drawer-save";
import type { CmsTheme } from "@/lib/cms/cms-theme.constants";
import {
  AboutUsContent,
  ArtistContent,
  CatalogueRaisonneGeneralContent,
  CatalogueRaisonneSection,
  CatalogueRaisonneSectionUpsert,
  CatalogueRaisonneSpecificContent,
  CmsPageDto,
  CmsThemeForm,
  ContactContent,
  CreateCmsPage,
  EShopContent,
  ExpertiseContent,
  GcuContent,
  GeneralSettings,
  HomepageContent,
  LegalNoticesContent,
  LoginContent,
  NewsItem,
  NewsPageContent,
  NewsPageEditContent,
  PrivacyPolicyContent,
  SelectionContent,
  SignUpContent,
  UpdateCmsPage,
} from "@/lib/cms/cms-types";
import { FetchedArtwork, SubmittedAuthenticationForm } from "@/lib/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { visibleT } from "@/lib/cms/cms-new-i18n";
import { SyntheticEvent } from "react";
import { FieldValues, Path, PathValue, UseFormReturn } from "react-hook-form";
import { mapFetchedArtworks } from "./artwork/artwork";
import { PaginatedReturn, PagingParameters } from "./dtos";

export const baseUrl = "back/api/cms";

export function wrapHandleSubmitWithAction<T extends FieldValues>(
  form: UseFormReturn<T>,
  action: () => void,
  ...submitArguments: Parameters<typeof form.handleSubmit>
) {
  const submit = form.handleSubmit(...submitArguments);

  return (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    action();
    submit(event);
  };
}

/**
 * Removes the fields that are not dirty or not defined in the fetched data
 * You can use @param baseFormRoute in case you have some nested fields, the function will then compare the @param fetchedData fields baseFormRoute nested fields
 */
export function removeCleanOrNotDefined<T extends FieldValues>(
  form: UseFormReturn<T>,
  fetchedData: Record<string, unknown> | undefined,
  baseFormRoute?: Path<T>,
) {
  const prependedBaseFormRoute = baseFormRoute ? `${baseFormRoute}.` : "";

  const allFields = baseFormRoute
    ? form.getValues(baseFormRoute)
    : form.getValues();

  if (allFields == undefined || typeof allFields !== "object") return;

  Object.keys(allFields).forEach((key) => {
    const dirtyField = form.getFieldState(
      (prependedBaseFormRoute + key) as Path<T>,
    )?.isDirty;

    if (fetchedData?.[key] == undefined && dirtyField === false) {
      form.setValue(
        (prependedBaseFormRoute + key) as Path<T>,
        undefined as PathValue<T, Path<T>>,
      );
    }
  });
}

/**
 *  Wraps the react form hook function but removes the non dirty fields from a multilingual form
 *
 * @param form : The react hook form
 * @param fetchedData
 * @param submitArguments
 * @returns
 */
/** Shown when the user clicks "save" with no dirty fields (no-op save). */
export function showCmsSaveSuccessToast() {
  toast({
    title: t("success"),
    description: t("homepageSuccessMessage"),
    variant: "success",
    duration: 4000,
  });
}

export function showCmsFormValidationErrorToast() {
  if (notifyDrawerAppearanceValidationFailed()) return;
  toast({
    title: t("error"),
    description: t("cms.appearance.error.validation"),
    variant: "error",
    duration: 3000,
  });
}

export const handleSubmitMultilingualForm = <T>(
  form: UseFormReturn<LocaleDict<T | undefined>>,
  fetchedData: LocaleDict<unknown> | undefined,
  ...submitArguments: Parameters<typeof form.handleSubmit>
) => {
  const [onValid, onInvalid, ...rest] = submitArguments;
  const submit = wrapHandleSubmitWithAction(
    form,
    () => removeCleanOrNotDefined(form, fetchedData),
    onValid,
    onInvalid ?? showCmsFormValidationErrorToast,
    ...rest,
  );

  return (event: SyntheticEvent<HTMLFormElement>) => {
    // Clicking "save" with no modified field would silently do nothing
    // (the page onSubmit only mutates dirty locales). Surface that instead.
    // The Apparence drawer save has its own no-op handling, so skip it there.
    if (
      !isDrawerAppearanceSavePending() &&
      Object.keys(form.formState.dirtyFields).length === 0
    ) {
      event.preventDefault();
      const uploadedKind = consumeCmsMediaUploaded();
      if (uploadedKind) {
        toast({
          variant: "success",
          description: t(cmsMediaTranslationKey("uploadSuccess", uploadedKind)),
          duration: 4000,
        });
      } else {
        showCmsSaveSuccessToast();
      }
      return;
    }
    submit(event);
  };
};

interface PageCreate<C> {
  data: CreateCmsPage<C>;
  lang: SupportedLocale;
}

const createPageGenerator = <C, R = CmsPageDto<C>>(
  slug: string,
): ((dataIn: PageCreate<C>) => Promise<R>) => {
  return async (dataIn: PageCreate<C>) => {
    const { data } = await api.post<R>(
      `${baseUrl}/${slug}/${dataIn.lang}`,
      dataIn.data,
    );
    return data;
  };
};

interface PageUpdate<C> {
  id: string;
  data: UpdateCmsPage<C>;
}

const updatePageGenerator = <C, R = CmsPageDto<C>>(
  slug: string,
): ((dataIn: PageUpdate<C>) => Promise<R>) => {
  return async (dataIn: PageUpdate<C>) => {
    const { data } = await api.patch<R>(
      `${baseUrl}/${slug}/${dataIn.id}`,
      dataIn.data,
    );
    return data;
  };
};

const pageMutationGenerator =
  <In, Out>(slug: string, func: (dataIn: In) => Promise<Out>) =>
  () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: func,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["cms", slug] });
        notifyDrawerAppearanceMutationResult(true);
      },
      onError: () => {
        notifyDrawerAppearanceMutationResult(false);
      },
    });
  };

const getFormDataGenerator = <Out>(slug: string) => {
  return async () => {
    const { data } = await api.get<LocaleDict<CmsPageDto<Out>>>(
      `back/api/cms/${slug}/`,
    );

    return data;
  };
};

const getFormDataAuthenticationGenerator = (slug: string) => {
  return async (cmsId: string) => {
    const { data } = await api.get<ArtworkAuthenticationCmsDto>(
      `back/api/cms/${slug}/${cmsId}`,
    );

    return data;
  };
};

const formDataQueryGenerator =
  <Out>(slug: string, func: () => Promise<Out>) =>
  () =>
    useQuery({
      queryKey: ["cms", slug],
      queryFn: func,
    });

const formDataQueryGeneratorWithParams =
  <Out, Params>(slug: string, func: (params: Params) => Promise<Out>) =>
  (params: Params) =>
    useQuery({
      queryKey: ["cms", slug, params],
      queryFn: () => func(params),
    });

export const createHomepage = createPageGenerator<HomepageContent>("homepage");
export const useCreateHomepage = pageMutationGenerator(
  "homepage",
  createHomepage,
);

export const updateHomepage = updatePageGenerator<HomepageContent>("homepage");
export const useUpdateHomepage = pageMutationGenerator(
  "homepage",
  updateHomepage,
);

const getHomepageFormData = getFormDataGenerator<HomepageContent>("homepage");
export const useGetHomepageFormData = formDataQueryGenerator(
  "homepage",
  getHomepageFormData,
);

const createArtist = createPageGenerator<ArtistContent>("artist");
export const useCreateArtist = pageMutationGenerator("artist", createArtist);

const updateArtist = updatePageGenerator<ArtistContent>("artist");
export const useUpdateArtist = pageMutationGenerator("artist", updateArtist);

const getArtistFormData = getFormDataGenerator<ArtistContent>("artist");
export const useGetArtistFormData = formDataQueryGenerator(
  "artist",
  getArtistFormData,
);

const createAboutPage = createPageGenerator<AboutUsContent>("about-us");
export const useCreateAboutPage = pageMutationGenerator(
  "about-us",
  createAboutPage,
);
const updateAboutPage = updatePageGenerator<AboutUsContent>("about-us");
export const useUpdateAboutPage = pageMutationGenerator(
  "about-us",
  updateAboutPage,
);

const getAboutPageFormData = getFormDataGenerator<AboutUsContent>("about-us");
export const useGetAboutPageFormData = formDataQueryGenerator(
  "about-us",
  getAboutPageFormData,
);

const createContact = createPageGenerator<ContactContent>("contact");
export const useCreateContact = pageMutationGenerator("contact", createContact);

const updateContact = updatePageGenerator<ContactContent>("contact");
export const useUpdateContact = pageMutationGenerator("contact", updateContact);

const getContactFormData = getFormDataGenerator<ContactContent>("contact");
export const useGetContactFormData = formDataQueryGenerator(
  "contact",
  getContactFormData,
);

const createSelectionPage = createPageGenerator<SelectionContent>("selection");
export const useCreateSelectionPage = pageMutationGenerator(
  "selection",
  createSelectionPage,
);
const updateSelectionPage = updatePageGenerator<SelectionContent>("selection");
export const useUpdateSelection = pageMutationGenerator(
  "selection",
  updateSelectionPage,
);

const updateSelectionItems = async (body: {
  selectedArtworks: string[];
  selectionSortBy?: string;
}) => {
  return api.patch("back/api/cms/selection/items", body);
};

export const useUpdateSelectionItems = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateSelectionItems,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cms", "selection"] });
    },
  });
};

const getSelectionFormData =
  getFormDataGenerator<SelectionContent>("selection");
export const useGetSelectionFormData = formDataQueryGenerator(
  "selection",
  getSelectionFormData,
);

const getSelectedArtworks = async () => {
  const { data } = await api.get<{
    artworks: FetchedArtwork[];
    sortBy: string;
  }>("back/api/cms/selection/items");

  return { ...data, artworks: data.artworks?.map(mapFetchedArtworks) };
};

export const useGetSelectedArtworks = () => {
  return useQuery({
    queryKey: ["cms", "selection", "items"],
    queryFn: getSelectedArtworks,
  });
};

const createNewsPage = createPageGenerator<
  NewsPageEditContent,
  NewsPageContent
>("news-page");
export const useCreateNewsPage = pageMutationGenerator(
  "news-page",
  createNewsPage,
);

const updateNewsPage = updatePageGenerator<
  NewsPageEditContent,
  NewsPageContent
>("news-page");
export const useUpdateNewsPage = pageMutationGenerator(
  "news-page",
  updateNewsPage,
);

const getNewsPageFormData = getFormDataGenerator<NewsPageContent>("news-page");
export const useGetNewspageFormData = formDataQueryGenerator(
  "news-page",
  getNewsPageFormData,
);

async function createNewsItem(
  dataNews: Omit<NewsItem, "attachments" | "createdAt" | "updatedAt">,
) {
  const { data } = await api.post("back/api/cms/news", dataNews);
  return data;
}

export const useCreateNewsItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createNewsItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["news"] });
      queryClient.invalidateQueries({ queryKey: ["cms", "news-page"] });
    },
  });
};

async function updateNewsItem(
  dataNews: Omit<NewsItem, "attachments" | "createdAt" | "updatedAt">,
) {
  const { data } = await api.patch(
    "back/api/cms/news/" + dataNews.id,
    dataNews,
  );
  return data;
}

export const useUpdateNewsItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateNewsItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["news"] });
      queryClient.invalidateQueries({ queryKey: ["cms", "news-page"] });
    },
  });
};

async function getAllNews() {
  const { data } = await api.get<NewsItem[]>("back/api/cms/news");
  return data;
}

export const useGetAllNews = () => {
  return useQuery({
    queryKey: ["news"],
    queryFn: getAllNews,
  });
};

async function deleteNews(id: string) {
  await api.delete("back/api/cms/news/" + id);
}

export const useDeleteNews = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteNews,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["news"] });
      queryClient.invalidateQueries({ queryKey: ["cms", "news-page"] });
    },
  });
};

export const createExpertise =
  createPageGenerator<ExpertiseContent>("expertise");
export const updateExpertise =
  updatePageGenerator<ExpertiseContent>("expertise");

export const useCreateExpertise = pageMutationGenerator(
  "expertise",
  createExpertise,
);

export const useUpdateExpertise = pageMutationGenerator(
  "expertise",
  updateExpertise,
);
const getExpertiseFormData =
  getFormDataGenerator<ExpertiseContent>("expertise");

export const useGetExpertiseFormData = formDataQueryGenerator(
  "expertise",
  getExpertiseFormData,
);

const getArtworkAuthenticationFormData = getFormDataAuthenticationGenerator(
  "artwork-authentication-cms",
);

export const useGetArtworkAuthenticationFormData =
  formDataQueryGeneratorWithParams(
    "artwork-authentication-cms",
    getArtworkAuthenticationFormData,
  );

const getCatalogueRaisonneSpecificArtworksFormData =
  getFormDataGenerator<CatalogueRaisonneSpecificContent>(
    "catalogue-raisonne/ARTWORKS",
  );
export const useCatalogueRaisonneeSpecificArtworksFormData =
  formDataQueryGenerator(
    "catalogue-raisonne/ARTWORKS",
    getCatalogueRaisonneSpecificArtworksFormData,
  );

const getCatalogueRaisonneSpecificMultiplesFormData =
  getFormDataGenerator<CatalogueRaisonneSpecificContent>(
    "catalogue-raisonne/MULTIPRINTS",
  );
export const useCatalogueRaisonneSpecificMutliplesFormData =
  formDataQueryGenerator(
    "catalogue-raisonne/MULTIPRINTS",
    getCatalogueRaisonneSpecificMultiplesFormData,
  );

const updateCatalogueRaisonneSpecifique =
  updatePageGenerator<CatalogueRaisonneSpecificContent>("catalogue-raisonne");
export const useUpdateCatalogueRaisonneSpecifique = pageMutationGenerator(
  "catalogue-raisonne",
  updateCatalogueRaisonneSpecifique,
);

const createCatalogueRaisonneSpecifique =
  createPageGenerator<CatalogueRaisonneSpecificContent>("catalogue-raisonne");
export const useCreateCatalogueRaisonneSpecifique = pageMutationGenerator(
  "catalogue-raisonne",
  createCatalogueRaisonneSpecifique,
);

const getCatalogueRaisonneGeneralFormData =
  getFormDataGenerator<CatalogueRaisonneGeneralContent>(
    "catalogue-raisonne-general",
  );
export const useCatalogueRaisonneGeneralFormData = formDataQueryGenerator(
  "catalogue-raisonne-general",
  getCatalogueRaisonneGeneralFormData,
);

const updateCatalogueRaisonneGeneral =
  updatePageGenerator<CatalogueRaisonneGeneralContent>(
    "catalogue-raisonne-general",
  );
export const useUpdateCatalogueRaisonneGeneral = pageMutationGenerator(
  "catalogue-raisonne-general",
  updateCatalogueRaisonneGeneral,
);

const createCatalogueRaisonneGeneral =
  createPageGenerator<CatalogueRaisonneGeneralContent>(
    "catalogue-raisonne-general",
  );
export const useCreateCatalogueRaisonneGeneral = pageMutationGenerator(
  "catalogue-raisonne-general",
  createCatalogueRaisonneGeneral,
);

export const fetchSections = async (cmsID: string, target: string) => {
  const response = await api.get<CatalogueRaisonneSection[]>(
    `back/api/cms/catalogue-raisonne/sections/${cmsID}/${target}`,
  );
  return response.data.map((section) => ({
    ...section,
    categoryMultilingual:
      section.categoryMultilingual ?? constructLocaleDict(() => ""),
  }));
};

export const useSections = (cmsID: string, target: string) => {
  return useQuery({
    queryKey: ["sections", cmsID, target],
    queryFn: () => fetchSections(cmsID, target),
    staleTime: 5 * 60 * 1000,
  });
};

export const fetchCategories = async (list: string | undefined) => {
  if (!list) {
    return null;
  }
  const response = await api.get(`back/api/cms/categories/${list}`);
  console.log("categories -> ", response.data);
  return response.data;
};

export const useCategories = (list: string | undefined) => {
  return useQuery({
    queryKey: ["categories", list],
    queryFn: () => fetchCategories(list),
    enabled: !!list,
  });
};

interface CatalogueRaisonneDTO {
  cmsId: string;
  type: string;
  decoupages: CatalogueRaisonneSectionUpsert[];
  target: string;
}

const updateCatalogueRaisonneSections = async (data: CatalogueRaisonneDTO) => {
  const response = await api.post(`/back/api/cms/catalogue-raisonne`, data);
  return response.data;
};

export const useUpdateCatalogueRaisonneSections = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateCatalogueRaisonneSections,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["catalogueRaisonne"] });
    },
  });
};

const getLegalNoticesFormData =
  getFormDataGenerator<LegalNoticesContent>("legal-notices");
export const useLegalNoticesFormData = formDataQueryGenerator(
  "legal-notices",
  getLegalNoticesFormData,
);

const createLegalNoticesPage =
  createPageGenerator<LegalNoticesContent>("legal-notices");
export const useCreateLegalNoticesPage = pageMutationGenerator(
  "legal-notices",
  createLegalNoticesPage,
);

const updateLegalNoticesPage =
  updatePageGenerator<LegalNoticesContent>("legal-notices");
export const useUpdateLegalNoticesPage = pageMutationGenerator(
  "legal-notices",
  updateLegalNoticesPage,
);

const getGcuFormData = getFormDataGenerator<GcuContent>("gcu");
export const useGcuFormData = formDataQueryGenerator("gcu", getGcuFormData);

const createGcuPage = createPageGenerator<GcuContent>("gcu");
export const useCreateGcuPage = pageMutationGenerator("gcu", createGcuPage);

const updateGcuPage = updatePageGenerator<GcuContent>("gcu");
export const useUpdateGcuPage = pageMutationGenerator("gcu", updateGcuPage);

const getPrivacyPolicyFormData =
  getFormDataGenerator<PrivacyPolicyContent>("privacy-policy");
export const usePrivacyPolicyFormData = formDataQueryGenerator(
  "privacy-policy",
  getPrivacyPolicyFormData,
);

const createPrivacyPolicyPage =
  createPageGenerator<PrivacyPolicyContent>("privacy-policy");
export const useCreatePrivacyPolicyPage = pageMutationGenerator(
  "privacy-policy",
  createPrivacyPolicyPage,
);

const updatePrivacyPolicyPage =
  updatePageGenerator<PrivacyPolicyContent>("privacy-policy");
export const useUpdatePrivacyPolicyPage = pageMutationGenerator(
  "privacy-policy",
  updatePrivacyPolicyPage,
);

const createEshopPage = createPageGenerator<EShopContent>("e-shop");
export const useCreateEshopPage = pageMutationGenerator(
  "e-shop",
  createEshopPage,
);

const updateEshopPage = updatePageGenerator<EShopContent>("e-shop");
export const useUpdateEshopPage = pageMutationGenerator(
  "e-shop",
  updateEshopPage,
);

const getEshopFormData = getFormDataGenerator<EShopContent>("e-shop");
export const useGetEshopFormData = formDataQueryGenerator(
  "e-shop",
  getEshopFormData,
);

const getLoginFormData = getFormDataGenerator<LoginContent>("login");
export const useLoginFormData = formDataQueryGenerator(
  "login",
  getLoginFormData,
);
const updateLoginPage = updatePageGenerator<LoginContent>("login");
export const useUpdateLoginPage = pageMutationGenerator(
  "login",
  updateLoginPage,
);
const createLoginPage = createPageGenerator<LoginContent>("login");
export const useCreateLoginPage = pageMutationGenerator(
  "login",
  createLoginPage,
);

const getSignUpFormData = getFormDataGenerator<SignUpContent>("signup");
export const useSignUpFormData = formDataQueryGenerator(
  "sign-up",
  getSignUpFormData,
);
const updateSignUpPage = updatePageGenerator<SignUpContent>("signup");
export const useUpdateSignUpPage = pageMutationGenerator(
  "sign-up",
  updateSignUpPage,
);

const createSignUpPage = createPageGenerator<SignUpContent>("signup");
export const useCreateSignUpPage = pageMutationGenerator(
  "sign-up",
  createSignUpPage,
);

const getGeneralSettingsFormData = async () => {
  const { data } = await api.get<GeneralSettings>(
    "back/api/cms/general-settings",
  );
  return data;
};

// Query dédiée (et non le générateur partagé) pour pouvoir poser un staleTime :
// la config de langues est lue par chaque champ multilingue de la page, donc par
// une dizaine d'observers. Sans staleTime, chaque montage d'observer relançait un
// refetch, et comme la réponse contient des URL S3 signées, `data` changeait
// d'identité à chaque fois. La config est de toute façon invalidée explicitement
// après sauvegarde (useUpdateGeneralSettings + fetchMainLanguage).
export const useGeneralSettingsFormData = () =>
  useQuery({
    queryKey: ["cms", "general-settings"],
    queryFn: getGeneralSettingsFormData,
    staleTime: 5 * 60 * 1000,
  });

const updateGeneralSettings = async (data: GeneralSettings) => {
  const response = await api.patch("back/api/cms/general-settings", data);
  return response.data;
};

const getCmsTheme = async () => {
  const { data } = await api.get<CmsTheme>("back/api/cms/theme");
  return data;
};

export const useGetCmsTheme = () => {
  return useQuery({
    queryKey: ["cms", "theme"],
    queryFn: getCmsTheme,
    // Même logique que general-settings : beaucoup d'observers (layout +
    // toolbars TipTap). Sans staleTime, chaque remount refetchait le thème
    // et alimentait la boucle vignettes CR (URLs signées).
    staleTime: 5 * 60 * 1000,
  });
};

const updateCmsTheme = async (data: CmsThemeForm & { id?: string }) => {
  const { data: response } = await api.patch<CmsTheme>(
    "back/api/cms/theme",
    data,
  );
  return response;
};

export const useUpdateCmsTheme = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateCmsTheme,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["cms", "theme"] });
    },
  });
};

export const useUpdateGeneralSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateGeneralSettings,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["cms", "general-settings"] });
      // Language config affects MultilingualField layout on every CMS page.
      await queryClient.invalidateQueries({ queryKey: ["cms"] });
      await fetchMainLanguage();
    },
  });
};

type CmsFaviconResponse = {
  favicon: string | null;
  faviconUrl: string | null;
};

export function useGetCmsFavicon() {
  return useQuery({
    queryKey: ["cms", "favicon"],
    queryFn: async () => {
      const { data } = await api.get<CmsFaviconResponse>(
        "back/api/cms/favicon",
      );
      return data;
    },
  });
}

export function useUploadCmsFavicon() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("favicon", file);
      const { data } = await api.patch<CmsFaviconResponse>(
        "back/api/cms/favicon",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cms", "favicon"] });
      queryClient.invalidateQueries({ queryKey: ["cms", "general-settings"] });
    },
  });
}

export function useDeleteCmsFavicon() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.delete<CmsFaviconResponse>(
        "back/api/cms/favicon",
      );
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData<CmsFaviconResponse>(["cms", "favicon"], {
        favicon: data?.favicon ?? null,
        faviconUrl: data?.faviconUrl ?? null,
      });
      void queryClient.invalidateQueries({ queryKey: ["cms", "favicon"] });
      void queryClient.invalidateQueries({
        queryKey: ["cms", "general-settings"],
      });
    },
  });
}

type CmsMaintenanceImageResponse = {
  maintenanceImage: string | null;
  maintenanceImageUrl: string | null;
};

export function useGetMaintenanceImage() {
  return useQuery({
    queryKey: ["cms", "maintenance-image"],
    queryFn: async () => {
      const { data } = await api.get<CmsMaintenanceImageResponse>(
        "back/api/cms/maintenance-image",
      );
      return data;
    },
  });
}

export function useUploadMaintenanceImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("image", file);
      const { data } = await api.patch<CmsMaintenanceImageResponse>(
        "back/api/cms/maintenance-image",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["cms", "maintenance-image"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["cms", "general-settings"],
      });
    },
  });
}

export function useDeleteMaintenanceImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.delete<CmsMaintenanceImageResponse>(
        "back/api/cms/maintenance-image",
      );
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData<CmsMaintenanceImageResponse>(
        ["cms", "maintenance-image"],
        {
          maintenanceImage: data?.maintenanceImage ?? null,
          maintenanceImageUrl: data?.maintenanceImageUrl ?? null,
        },
      );
      void queryClient.invalidateQueries({
        queryKey: ["cms", "maintenance-image"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["cms", "general-settings"],
      });
    },
  });
}

const getCountAllItems = async (key: string) => {
  const { data } = await api.get<number>(`back/api/cms/count-all-items/${key}`);
  return data;
};

export const useCountAllItems = (key: string) => {
  return useQuery({
    queryKey: ["countAllItems", key],
    queryFn: () => getCountAllItems(key),
  });
};

export async function getPricesInsertion() {
  try {
    const { data } = await api.get('/back/api/prices/insertion');
    return data;
  } catch (error) {
    console.error('Get prices error:', error);
  }
}

export async function getPrices() {
  try {
    const { data } = await api.get("/back/api/prices");
    return data;
  } catch (error) {
    console.error("Get prices error:", error);
  }
}

export const usePrices = () => {
  return useQuery({
    queryKey: ["prices"],
    queryFn: getPrices,
  });
};

export const usePricesInsertion = () => {
  return useQuery({
    queryKey: ['pricesInsertions'],
    queryFn: getPricesInsertion,
  });
};


export const getSubmittedAuthenticationForms = async (pagination: PagingParameters & { search?: string; artworkType?: string; instanceId: string }) => {
  const { instanceId, ...params } = pagination;
  const { data } = await api.get<PaginatedReturn<SubmittedAuthenticationForm>>(`back/api/artwork-authentication/${instanceId}`, { params });
  return data;
};

export const useGetSubmittedAuthenticationForms = (pagination: PagingParameters & { search?: string; artworkType?: string; instanceId: string }) => {
  return useQuery({
    queryKey: ['submittedAuthenticationForms', pagination],
    queryFn: () => getSubmittedAuthenticationForms(pagination),
  });
};

export const downloadPdf = async (id?: string) => {
  if (!id) return;
  try {
    const response = await api.post('/back/api/artwork-authentication/pdf-document/authentication', {
      artworkAuthenticationId: id,
      generateAndSendByEmail: false,
      secretKey: import.meta.env.VITE_PDF_SECRET_KEY,
    }, {
      responseType: 'blob'
    });

    const url = window.URL.createObjectURL(response.data);
    const downloadLink = document.createElement("a");
    downloadLink.href = url;
    downloadLink.download = `authentication.pdf`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    window.URL.revokeObjectURL(url);
    toast({
      title: visibleT("success", "Succès"),
      description: visibleT("cms.authentication.detail.pdf.downloaded", "PDF téléchargé avec succès"),
      variant: "success",
    });
  } catch (error) {
    toast({
      title: visibleT("error", "Erreur"),
      description: visibleT("cms.authentication.detail.pdf.error", "Erreur lors du téléchargement du PDF"),
      variant: "error",
    });
    console.error("Error downloading PDF:", error);
  }
};
